import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { INPUT_FIELD_ERROR_CLASSNAME, LARGE_INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname"
import { Path, UseFormReturn } from "react-hook-form"
import { z } from "zod"
import { SelectInputField as SelectInputFieldHelper } from "@/helpers/selectInputField"
import { SelectOption } from "@/types/inputField"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SelectInputField = <Tschema extends z.ZodType<any, any, any>>({
  form,
  name,
  label,
  required = false,
  infoContent,
  error,
  selectOptions,
  isDisabled = false,
  additionalClassName,
  placeholderText
}: {
  form: UseFormReturn<z.infer<Tschema>>,
  schema: Tschema,
  name: Path<z.TypeOf<Tschema>>
  label: string,
  required?: boolean,
  error?: boolean
  infoContent?: React.ReactNode,
  selectOptions: SelectOption[] | string[],
  isDisabled?: boolean,
  additionalClassName?: string,
  placeholderText?: string
}) => {
  const inputFieldClassName = `${LARGE_INPUT_FIELD_CLASSNAME} ${additionalClassName}`

  const getFieldDisplayValue = (fieldValue: string) => {
    const option = selectOptions?.find(opt => (opt as SelectOption).id === fieldValue)
    if (option) {
      return (option as SelectOption).value
    }
    return fieldValue
  }

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel
            className="flex justify-start font-semibold text-primary"
            isRequired={required}
            infoContent={infoContent}
          >
            {label}
          </FormLabel>
          <FormControl>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <SelectTrigger
                disabled={isDisabled}
                className={`${inputFieldClassName} ${error && INPUT_FIELD_ERROR_CLASSNAME}`}
              >
                <SelectValue
                  placeholder={placeholderText ??
                    SelectInputFieldHelper.getFormattedOption(
                      SelectInputFieldHelper.getPlaceHolderText(getFieldDisplayValue(field.value), 'Select')
                    )
                  }
                >
                  {getFieldDisplayValue(field.value) ?? ''}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className={inputFieldClassName}>
                {selectOptions?.map((t, i) => {
                  if (typeof t === 'string') {
                    return (
                      <SelectItem key={t + i} value={t}>
                        {SelectInputFieldHelper.getFormattedOption(t)}
                      </SelectItem>
                    )
                  } else {
                    return (
                      <SelectItem key={i} value={t.id} className="flex flex-col items-start justify-start">
                        <p>{t.value}</p>
                        {t?.description ? <span className="text-sm text-secondary">{t?.description}</span> : null}
                      </SelectItem>
                    )
                  }
                })}
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage
            showIcon={true}
            className="flex flex-row justify-start"
          />
        </FormItem>
      )}
    />
  )
}
